/**
 * Settings → Pressure Testing → Customer ticket preferences (PT-5,
 * fieldiq-api#932 `PUT /customers/:customerId/settings`; PT-6, api#939
 * `GET /customers/settings`).
 *
 * Every customer, with the ticket mode a NEW job for them gets: their own
 * setting, else the company default. One read lists every customer with its
 * mode; if it fails, the whole table says so — no row is shown as "Per job".
 *
 * Changing a row PUTs it; "Company default" PUTs `null`, clearing the
 * customer's own setting. A job snapshots its mode when it is created, so this
 * never moves an existing job — that is changed on the job, until its first
 * ticket.
 *
 * Lives here, in Pressure Testing's settings, and not on the shared Customers
 * screen, which every vertical uses. Reads and writes are PT_DISPATCH.
 */
import React, { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { usePtCustomerSettingsList, useUpdatePtCustomerSettings } from '../../hooks/usePressureTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchPt, PT_TICKET_MODE_LABEL, ptTicketModeLabel } from '../../lib/pressure-testing';
import { PT_TICKET_MODES, type PtTicketMode } from '../../types/pressure-testing';
import { PT_INPUT } from './PtBits';
import { ptWriteError } from './PtJobDialogs';

export function PtCustomerTicketModes({ companyDefault }: { companyDefault: string | null | undefined }) {
  const { user } = useAuth();
  const canWrite = canDispatchPt(user?.role);
  const customers = usePtCustomerSettingsList();
  const list = customers.data?.data ?? [];
  const save = useUpdatePtCustomerSettings();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const failure = queryLoadFailure(customers);

  const change = (customerId: string, name: string, value: string) => {
    setError(null);
    setBusyId(customerId);
    const mode = value === '' ? null : (value as PtTicketMode);
    save.mutate(
      { customerId, ticketMode: mode },
      {
        onSuccess: (res) => toast.success(`${name}: ${ptTicketModeLabel(res.data?.ticket_mode)}${mode === null ? ' (company default)' : ''}`),
        onError: (err) => setError(`${name}: ${ptWriteError(err, 'the ticket mode was not saved.').message}`),
        onSettled: () => setBusyId(null),
      },
    );
  };

  const q = search.trim().toLowerCase();
  const shown = list.filter((row) => !q || (row.customer_name ?? '').toLowerCase().includes(q));

  return (
    <div className="space-y-4" data-testid="pt-customer-ticket-modes">
      <p className="text-sm text-fieldiq-text">
        Per job: one field ticket when the job completes. Per day: a ticket for each work day, closed out in the field. Company default:{' '}
        <span className="font-bold" data-testid="pt-customer-modes-default">{ptTicketModeLabel(companyDefault)}</span>. A change applies to jobs created
        after it.
      </p>
      {!canWrite && <p className="text-xs text-fieldiq-muted">Only an admin, office staff, a supervisor or a dispatcher can change these.</p>}
      {error && (
        <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}

      <label className="relative block max-w-md">
        <span className="sr-only">Search customers</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers"
          className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
        />
      </label>

      <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
        {customers.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading customers" />
          </div>
        ) : failure ? (
          <div className="p-4">
            <LoadFailureNotice error={queryFailureError(customers)} kind={failure} subject="customers' ticket modes" />
          </div>
        ) : list.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fieldiq-text">There are no customers on this account.</p>
        ) : shown.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fieldiq-text">No customer matches this search.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <tr className="border-l-4 border-l-transparent">
                  <th scope="col" className="px-4 py-2">Customer</th>
                  <th scope="col" className="px-4 py-2">Ticket mode</th>
                  <th scope="col" className="px-4 py-2">Set by</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => {
                  const c = { id: row.customer_id, name: row.customer_name };
                  return (
                    <tr key={c.id} data-testid="pt-customer-mode-row" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                      <td className="px-4 py-2.5 text-sm font-semibold text-white">{c.name || '—'}</td>
                      <td className="px-4 py-2.5">
                        {canWrite ? (
                          <div className="flex items-center gap-2">
                            <select
                              className={`${PT_INPUT} w-56`}
                              value={row.customer_ticket_mode ?? ''}
                              onChange={(e) => change(c.id, c.name || 'Customer', e.target.value)}
                              disabled={busyId === c.id}
                              aria-label={`${c.name || 'Customer'} ticket mode`}
                              data-testid="pt-customer-mode-select"
                            >
                              <option value="">Company default ({ptTicketModeLabel(row.company_default_ticket_mode)})</option>
                              {PT_TICKET_MODES.map((m) => (
                                <option key={m} value={m}>
                                  {PT_TICKET_MODE_LABEL[m]}
                                </option>
                              ))}
                            </select>
                            {busyId === c.id && <Loader2 className="size-4 animate-spin text-fieldiq-muted" aria-hidden="true" />}
                          </div>
                        ) : (
                          <span className="text-sm text-white">{ptTicketModeLabel(row.ticket_mode)}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-fieldiq-muted" data-testid="pt-customer-mode-source">
                        {row.source === 'customer' ? 'This customer' : row.source === 'company_default' ? 'Company default' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {list.length > 0 && <p className="text-xs text-fieldiq-muted">{q ? `Showing ${shown.length} of ${list.length} customers` : `${list.length} customer${list.length === 1 ? '' : 's'}`}</p>}
    </div>
  );
}
